import React from 'react';
import {
  compose,
  withPropsOnChange,
  defaultProps,
  withHandlers,
  branch,
  getContext,
  onlyUpdateForKeys,
  withStateHandlers,
} from 'recompose';
import cx from 'classnames';
import { connect } from 'react-redux';
import Icon from 'components/Icon';
import PropTypes from 'prop-types';
import { Typo, Header, Badge } from 'components/Layout';
import getIn from 'redux-form/es/structure/plain/getIn';
import { isEmpty } from 'lodash';
import { withReduxForm } from 'redux-form/es/ReduxFormContext';
import Spinner from 'components/Spinner';

import styles from './styles.css';

const getClassName = (name) =>
  ({
    Header: styles.header,
    Footer: styles.footer,
    Body: styles.body,
  }[name]);

export type Props = {
  index?: number;
  isActive?: boolean;
  isComplete?: boolean;
  children: any;
  disabled?: boolean;
  locked?: boolean;
  title?: string | JSX.Element;
  description?: string;
  fields?: string[];
  theme?: 'dark' | 'light' | 'grey';
  className?: string;
  bodyClassName?: string;
  badge?: string;
  withoutIcon?: boolean;
  loading?: boolean;
  hidable?: boolean;
  alwaysRenderContent?: boolean;
  style?: any;
  iconName?: string;
  onIconClick?: (e: React.MouseEvent) => void;
};

export type OwnProps = Props & {
  theme: 'dark' | 'light' | 'grey';
  hasError?: boolean;
  onClick: () => void;
};

export { default as withBlocks } from './withBlocks';

const getIcon = (error, complete, index) =>
  (error && (
    <Icon
      name="error-outline"
      className={styles.error}
      width={24}
      height={24}
    />
  )) ||
  (complete && (
    <Icon
      name="circle-check-filled"
      className={styles.check}
      width={24}
      height={24}
    />
  )) ||
  index;

const getSyncError = (syncErrors: Object, name: string) => {
  const error = getIn(syncErrors, name);
  // Because the error for this field might not be at a level in the error structure where
  // it can be set directly, it might need to be unwrapped from the _error property
  return error && error._error ? error._error : error;
};

export const Block = compose<OwnProps, Props>(
  defaultProps({ theme: 'grey' }),
  withPropsOnChange(['children'], ({ children }) => ({
    children: React.Children.map(
      children,
      (c: React.ReactElement<any>) =>
        c &&
        React.cloneElement(c, {
          className: getClassName(c.type.displayName),
        })
    ),
  })),
  branch(
    ({ fields }) => !!fields,
    compose(
      withReduxForm,
      connect((state, ownProps) => {
        const {
          fields,
          _reduxForm: { initialValues, getFormState },
        } = ownProps;
        const formState = getFormState(state);
        return {
          hasError: fields.some((f) => getIn(formState, `submitErrors.${f}`)),
          isComplete: !fields.some((f) => {
            const value = getIn(formState, `values.${f}`);
            return !value || isEmpty(value);
          }),
        };
      })
    )
  ),
  branch(
    ({ index }) => !!index,
    compose(
      getContext({
        activeBlock: PropTypes.number,
        activateBlock: PropTypes.func,
      }),
      withPropsOnChange(
        ['activeBlock', 'index'],
        ({ activeBlock, index, isComplete }) => ({
          isActive: activeBlock === index,
        })
      )
    )
  ),
  withHandlers({
    onClick:
      ({ activateBlock, index }) =>
      () =>
        activateBlock && activateBlock(index),
  }),
  branch(
    ({ hidable }) => hidable,
    withStateHandlers(
      { isActive: false },
      {
        onClick:
          ({ isActive }) =>
          () => ({ isActive: !isActive }),
      }
    )
  ),
  onlyUpdateForKeys(['children', 'isComplete', 'isActive', 'hasError', 'index'])
)(
  ({
    children,
    disabled,
    locked,
    theme,
    title,
    className,
    description,
    bodyClassName,
    isActive,
    isComplete,
    hasError,
    onClick,
    index,
    badge,
    withoutIcon,
    loading,
    hidable = false,
    alwaysRenderContent = true,
    style,
    iconName = 'edit',
    onIconClick,
  }) => {
    const isOpened = hidable ? isActive : isActive || !index;

    return (
      <section
        className={cx(
          styles.root,
          styles[theme],
          className,
          isActive && styles.active,
          hasError && styles.hasError,
          disabled && styles.disabled,
          hidable && styles.hidable
        )}
        style={style}
      >
        {(index || hidable) && (
          <button
            className={styles.header}
            onClick={
              hidable || (!loading && !isActive && !disabled) ? onClick : void 0
            }
          >
            {!withoutIcon && (
              <div className={cx(styles.status, isComplete && styles.complete)}>
                {getIcon(hasError, isComplete, index)}
              </div>
            )}
            {title && (
              <Typo.Title
                size="small"
                style={{ marginBottom: 0, position: 'relative' }}
              >
                {title}
                {badge && <span className={styles.badge}>{badge}</span>}
              </Typo.Title>
            )}
            <div className={styles.divider} />
            {loading ? (
              <div className={styles.spinnerRoot}>
                <Spinner className={styles.spinner} />
              </div>
            ) : (
              !locked && (
                <Icon
                  onClick={(e) => {
                    if (onIconClick) {
                      e.preventDefault();
                      e.stopPropagation();
                      onIconClick(e);
                    }
                  }}
                  name={iconName}
                  className={styles.edit}
                  width={24}
                  height={24}
                />
              )
            )}
          </button>
        )}
        {(!!alwaysRenderContent || isOpened) && (
          <div
            style={{
              display: isOpened ? 'block' : 'none',
            }}
            className={styles.container}
          >
            {children}
          </div>
        )}
      </section>
    );
  }
);
